import type { openai } from '@ai-sdk/openai';
import type { novaToolDefs, outlookToolDefs } from '@newfront-insurance/ai-api';
import { ChainOfThoughtSearchResult, ChainOfThoughtSearchResults, SourceBadge } from '@newfront-insurance/nova-core';
import type { InferUITool } from 'ai';
import { useState } from 'react';

import { useSkillsSafe } from '../contexts/skills-context';
import type { CoTStep } from '../lib/chain-of-thought-utils';
import { type CoTSearchResult, getField, MAX_OUTPUT_LENGTH } from '../lib/chain-of-thought-utils';
import type { SourcePart, SourcesMap } from '../lib/sources';

type ToolIO<T extends keyof typeof novaToolDefs> = InferUITool<(typeof novaToolDefs)[T]>;
type OutlookToolIO<T extends keyof typeof outlookToolDefs> = InferUITool<(typeof outlookToolDefs)[T]>;
type ShellToolIO = InferUITool<ReturnType<typeof openai.tools.shell>>;
type DataExplorerOutputWithSources = ToolIO<'dataExplorer'>['output'] & {
  sources?: Array<{
    sourceId: string;
    url: string;
  }>;
};

type NovaToolInputUnion = {
  [K in keyof typeof novaToolDefs]: { toolName: K; input: ToolIO<K>['input'] };
}[keyof typeof novaToolDefs];
type OutlookToolInputUnion = {
  [K in keyof typeof outlookToolDefs]: { toolName: K; input: OutlookToolIO<K>['input'] };
}[keyof typeof outlookToolDefs];
export type CoTToolInputProps = (NovaToolInputUnion | OutlookToolInputUnion) & { priorSteps?: CoTStep[] };

/** Look up a filename from prior documentSearch outputs by fileId */
function findFilenameFromPriorSteps(
  fileId: string,
  priorSteps: CoTStep[],
): { filename: string; link?: string } | undefined {
  for (const step of priorSteps) {
    if (step.type !== 'tool' || step.toolName !== 'documentSearch' || !step.output) continue;
    const { results } = step.output as ToolIO<'documentSearch'>['output'];
    if (!Array.isArray(results)) continue;
    const match = results.find((r) => r.fileId === fileId);
    if (match) return { filename: match.filename, link: match.navigatorUrl };
  }
  return undefined;
}

const MAX_CODE_LINES = 10;

function TruncatedCode({ code }: { code: string }) {
  const [expanded, setExpanded] = useState(false);
  const lines = code.split('\n');
  const isTruncated = lines.length > MAX_CODE_LINES;
  const displayCode = isTruncated && !expanded ? `${lines.slice(0, MAX_CODE_LINES).join('\n')}\n…` : code;

  return (
    <div>
      <pre className="bg-muted overflow-x-auto rounded-md p-2">
        <code className="font-mono text-xs whitespace-pre">{displayCode}</code>
      </pre>
      {isTruncated && (
        <button
          type="button"
          className="text-primary mt-1 cursor-pointer text-xs hover:underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'show less' : `show all ${lines.length} lines`}
        </button>
      )}
    </div>
  );
}

function TruncatedText({ text, maxWords }: { text: string; maxWords: number }) {
  const [expanded, setExpanded] = useState(false);
  const words = text.split(/\s+/);
  const isTruncated = words.length > maxWords;

  if (!isTruncated) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {expanded ? text : `${words.slice(0, maxWords).join(' ')} [...]`}{' '}
      <button
        type="button"
        className="text-primary cursor-pointer text-xs hover:underline"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'show less' : 'see more'}
      </button>
    </span>
  );
}

function LoadSkillDescription({ output }: { output: ToolIO<'loadSkill'>['output'] }) {
  const skillsContext = useSkillsSafe();
  const description = skillsContext?.getSkillDescription({
    skillId: output.skillId,
    name: output.name,
  });

  if (description) {
    return <p>{description}</p>;
  }

  return output.name ? <p>{output.name}</p> : null;
}

/** Renders tool input as a description for a CoT step — returns null when no custom renderer exists */
export function CoTToolInput({ toolName, input, priorSteps }: CoTToolInputProps) {
  // Shell tool is an OpenAI provider tool (not in novaToolDefs/outlookToolDefs)
  if (toolName === ('shell' as string)) {
    const commands = getField<string[]>(input, 'action', 'commands');
    if (!commands?.length) return null;
    return <TruncatedCode code={commands.join('\n')} />;
  }

  switch (toolName) {
    case 'documentSearch': {
      const { query } = input;
      return query ? <span>Searching for &ldquo;{query}&rdquo;</span> : null;
    }
    case 'readFile': {
      const { fileId, pages } = input;
      const file = priorSteps ? findFilenameFromPriorSteps(fileId, priorSteps) : undefined;
      const pagesLabel = pages ? ` (pages ${pages})` : '';
      if (file) {
        return (
          <ChainOfThoughtSearchResults>
            {file.link ? (
              <a href={file.link} target="_blank" rel="noopener noreferrer">
                <ChainOfThoughtSearchResult>
                  {file.filename}
                  {pagesLabel}
                </ChainOfThoughtSearchResult>
              </a>
            ) : (
              <ChainOfThoughtSearchResult>
                {file.filename}
                {pagesLabel}
              </ChainOfThoughtSearchResult>
            )}
          </ChainOfThoughtSearchResults>
        );
      }
      return <span>Reading file{pagesLabel}</span>;
    }
    case 'processFiles': {
      const { context } = input;
      if (!context) return null;
      return <TruncatedText text={context} maxWords={100} />;
    }
    case 'dataExplorer': {
      const { lastMessageReasoning } = input;
      return lastMessageReasoning ? <span>{lastMessageReasoning}</span> : null;
    }
    case 'getMessage': {
      const { source } = input;
      if (source.type === 'mailbox' && source.mailbox) {
        return <span>Reading message from {source.mailbox}</span>;
      }
      return null;
    }
    case 'downloadEmailAttachments': {
      const { attachmentNames } = input;
      if (attachmentNames?.length) {
        return <span>Downloading {attachmentNames.join(', ')}</span>;
      }
      return <span>Downloading all attachments</span>;
    }
    // listInbox / listUnifiedMail: progress descriptions arrive via stepProgressMap,
    // so we only render input details that won't duplicate the progress message.
    case 'listInbox':
    case 'listUnifiedMail':
    case 'loadSkill':
      return null;
    default:
      return null;
  }
}

/** Renders search result badges from tool output — returns null when no extractor exists or no results */
export function CoTToolSearchResults({
  toolName,
  output,
  sourcesMap,
}: {
  toolName: string;
  output: unknown;
  sourcesMap?: SourcesMap;
}) {
  // Data explorer: render source badges + remaining count badge
  if (toolName === 'dataExplorer') {
    return <DataExplorerCoTSearchResults output={output} sourcesMap={sourcesMap} />;
  }

  const results = extractSearchResults(toolName, output);
  if (results.length === 0) return null;

  return (
    <ChainOfThoughtSearchResults>
      {results.map((result, index) =>
        result.link ? (
          <a key={result.link} href={result.link} target="_blank" rel="noopener noreferrer">
            <ChainOfThoughtSearchResult>{result.title}</ChainOfThoughtSearchResult>
          </a>
        ) : (
          <ChainOfThoughtSearchResult key={index}>{result.title}</ChainOfThoughtSearchResult>
        ),
      )}
    </ChainOfThoughtSearchResults>
  );
}

function DataExplorerCoTSearchResults({ output, sourcesMap }: { output: unknown; sourcesMap?: SourcesMap }) {
  const { data, sources } = output as DataExplorerOutputWithSources;

  if (!sources?.length && (!data || data.rowCount === 0)) return null;

  const sourceIndexMap = new Map(Array.from(sourcesMap?.keys() ?? []).map((id, i) => [id, i]));

  const matchedSources = (sources ?? [])
    .map((s: NonNullable<DataExplorerOutputWithSources['sources']>[number]) => {
      const source = sourcesMap?.get(s.sourceId);
      if (!source) return null;
      const globalIndex = sourceIndexMap.get(s.sourceId) ?? 0;
      return { source, index: globalIndex };
    })
    .filter((s): s is { source: SourcePart; index: number } => s !== null);

  const totalResults = data?.rowCount ?? 0;
  const remainingCount = totalResults - matchedSources.length;

  if (matchedSources.length === 0 && totalResults > 0) {
    return (
      <ChainOfThoughtSearchResults>
        <ChainOfThoughtSearchResult>
          {totalResults} result{totalResults === 1 ? '' : 's'} found
        </ChainOfThoughtSearchResult>
      </ChainOfThoughtSearchResults>
    );
  }

  if (matchedSources.length === 0) return null;

  return (
    <ChainOfThoughtSearchResults>
      {matchedSources.map(({ source, index }) => (
        <SourceBadge key={source.sourceId} source={source} index={index} size="sm" />
      ))}
      {remainingCount > 0 && (
        <ChainOfThoughtSearchResult>
          {remainingCount} other result{remainingCount === 1 ? '' : 's'}
        </ChainOfThoughtSearchResult>
      )}
    </ChainOfThoughtSearchResults>
  );
}

/** Renders tool output in a CoT step — uses custom renderer if available, falls back to truncated string */
export function CoTToolOutput({ toolName, output }: { toolName: string; output: unknown }) {
  switch (toolName) {
    case 'documentSearch': {
      const { searchSummary } = output as ToolIO<'documentSearch'>['output'];
      return searchSummary ? <p>{searchSummary}</p> : null;
    }
    case 'readFile':
      return null;
    case 'getMessage': {
      const { message, error } = output as OutlookToolIO<'getMessage'>['output'];
      if (error) return <p>{error}</p>;
      if (message?.subject) return <p>{message.subject}</p>;
      return null;
    }
    case 'downloadEmailAttachments': {
      const { downloadedFiles, unsupportedFiles, error } =
        output as OutlookToolIO<'downloadEmailAttachments'>['output'];
      if (error) return <p>{error}</p>;
      const downloaded = downloadedFiles ?? [];
      const unsupported = unsupportedFiles ?? [];
      if (downloaded.length === 0 && unsupported.length === 0) return null;
      return (
        <p>
          {downloaded.length > 0 && <>Downloaded {downloaded.map((f) => f.filename).join(', ')}</>}
          {unsupported.length > 0 && (
            <>
              {downloaded.length > 0 && <br />}
              {unsupported.length} unsupported file{unsupported.length === 1 ? '' : 's'}
            </>
          )}
        </p>
      );
    }
    case 'listInbox': {
      const { messages, error } = output as OutlookToolIO<'listInbox'>['output'];
      if (error) return <p>{error}</p>;
      const count = messages?.length ?? 0;
      if (count === 0) return <p>No messages found</p>;
      return (
        <p>
          Found {count} message{count === 1 ? '' : 's'}
        </p>
      );
    }
    case 'listUnifiedMail': {
      const { itemsBySource, error } = output as OutlookToolIO<'listUnifiedMail'>['output'];
      if (error) return <p>{error}</p>;
      const count = itemsBySource?.reduce((sum, source) => sum + (source.items?.length ?? 0), 0) ?? 0;
      if (count === 0) return <p>No messages found</p>;
      return (
        <p>
          Found {count} message{count === 1 ? '' : 's'}
        </p>
      );
    }
    case 'loadSkill': {
      return <LoadSkillDescription output={output as ToolIO<'loadSkill'>['output']} />;
    }
    case 'shell': {
      const shellOutput = output as ShellToolIO['output'];
      if (!shellOutput?.output?.length) return null;
      const text = shellOutput.output
        .map((entry) => [entry.stdout, entry.stderr].filter(Boolean).join(''))
        .filter(Boolean)
        .join('\n')
        .trim();
      if (!text) return null;
      return <TruncatedCode code={text} />;
    }
    case 'dataExplorer': {
      const { reasoning } = output as ToolIO<'dataExplorer'>['output'];
      return reasoning ? <p>{reasoning}</p> : null;
    }
    default: {
      if (typeof output === 'string') {
        const text = output.length > MAX_OUTPUT_LENGTH ? `${output.slice(0, MAX_OUTPUT_LENGTH)}…` : output;
        return <p>{text}</p>;
      }
      return null;
    }
  }
}

function extractSearchResults(toolName: string, output: unknown): CoTSearchResult[] {
  switch (toolName) {
    case 'documentSearch': {
      const { results } = output as ToolIO<'documentSearch'>['output'];
      if (!Array.isArray(results)) return [];
      return results
        .filter((r) => typeof r.filename === 'string')
        .map((r) => ({ title: r.filename, link: r.navigatorUrl }));
    }
    case 'loadSkill': {
      return [];
    }
    default:
      return [];
  }
}
